import { ProfileContext } from "context/ProfileContext";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { FaGlobe, FaRegCopy, FaRegUserCircle, FaRobot, FaUserEdit } from "react-icons/fa";
import { IoIosGlobe, IoMdClose } from "react-icons/io";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  _botCreation,
  _getActiveLanguage,
  _getBotDetails,
  _organizationDetails,
  _update_bot,
  _vendorCategories,
  _vendorUserName,
} from "Services/api";
import KnowledgeSourcesPanel from "./components/KnowledgeSourcesPanel";
import OwnerEmailLinkForm from "./components/OwnerEmailLinkForm";
import BotQrCard from "./components/BotQrCard";
import { getDefaultBotName } from "constants/bot";
import { updateStoredBot } from "utils/session";
import { tokenizeMessage } from "utils/messageFormat";

const BASE_URL = process.env.REACT_APP_CHATBOT_BASE_URL;
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const SUPPORTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const PARTNER_CATEGORY_NAME = "Plist Partner Organization";
const LODGING_BUSINESS_TYPES = [
  { value: "hotel", label: "Hotel", inventoryLabel: "Room" },
  { value: "resort", label: "Resort", inventoryLabel: "Room / Suite" },
  { value: "motel", label: "Motel", inventoryLabel: "Room" },
  { value: "guest_house", label: "Guest House", inventoryLabel: "Room" },
  { value: "inn", label: "Inn", inventoryLabel: "Room" },
  { value: "vacation_rental", label: "Vacation Rental", inventoryLabel: "Unit" },
  { value: "shortlet", label: "Shortlet", inventoryLabel: "Apartment" },
  { value: "serviced_apartment", label: "Serviced Apartment", inventoryLabel: "Apartment" },
  { value: "villa", label: "Villa", inventoryLabel: "Villa" },
  { value: "hostel", label: "Hostel / Shared Room", inventoryLabel: "Bed / Room" },
  { value: "lodge", label: "Lodge", inventoryLabel: "Room" },
  { value: "cabin", label: "Cabin", inventoryLabel: "Cabin" },
  { value: "other", label: "Other", inventoryLabel: "Space / Unit" },
];

const getImageFileError = (file) => {
  if (!SUPPORTED_IMAGE_TYPES.has(file?.type)) {
    return "Choose a JPEG, PNG, WebP, or GIF image.";
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return "Image size must not exceed 5 MB.";
  }
  return "";
};

const withoutImageFiles = (data = {}) =>
  Object.fromEntries(
    Object.entries(data).filter(([key]) => key !== "avatar_file" && key !== "logo_file")
  );

const isBrowserBlobUrl = (value) => /^blob:/i.test(String(value || "").trim());

// Default welcome message, seeded from the organization name. Mirrors the copy
// the guest sees first in the live widget. The blank lines are real "\n\n"
// breaks — the preview bubble tokenizes them into <br/> the same way the bot does.
const buildWelcomeMessage = (organizationName) => {
  const propertyName = String(organizationName || "").trim() || "our property";
  return (
    `Welcome to ${propertyName}.\n\n` +
    "I can help you book a room, check or manage your reservation, request guest services, " +
    "make payments, or answer questions about your stay.\n\n" +
    "How would you like to continue?"
  );
};

const Marketplace = () => {
  const [bgColor, setBgColor] = useState("#ccc");
  const profileData = useContext(ProfileContext);
  const [orgName, setOrgName] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("Hi! How can we help?");
  const [chatHeader, setChatHeader] = useState("Loading..."); // Start with loading state
  const [isHeaderLoading, setIsHeaderLoading] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [organizationType, setOrganizationType] = useState("");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [categoryList, setCategoryList] = useState();
  const [isBotCreated, setIsBotCreated] = useState(false);
  const [botId, setBotId] = useState();
  const [scriptTag, setScriptTag] = useState("");
  const [urlTag, seturlTag] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const avatarOptions = [
    "https://aichatbot-v1.spaincentral.cloudapp.azure.com/static_avatar/avatar-1.png",
    "https://aichatbot-v1.spaincentral.cloudapp.azure.com/static_avatar/avatar-2.png",
    "https://aichatbot-v1.spaincentral.cloudapp.azure.com/static_avatar/avatar-3.png",
    "https://aichatbot-v1.spaincentral.cloudapp.azure.com/static_avatar/avatar-4.png",
    "https://aichatbot-v1.spaincentral.cloudapp.azure.com/static_avatar/avatar-5.png",
    // "https://aichatbot-v1.spaincentral.cloudapp.azure.com/static_avatar/avatar-6.png",
    // "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTD7fPCXfwHzx4bYeKVTHm9iZsod_H2CsVikSNsadVA-TdvN-oU5CopzA3PqXojFNiIIwo186efHGh7yVsOSij1D2T6G8Qh-uB5tp7Ja92X&s=10"
  ];
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Bumped when the bot's owners change so KnowledgeSourcesPanel refetches its
  // (partly owner-derived) sources.
  const [knowledgeRefreshKey, setKnowledgeRefreshKey] = useState(0);
  const [activeLanguages, setActiveLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState(
    (activeLanguages && activeLanguages[0]?.code) || ""
  );
  const [selectedBot, setSelectedBot] = useState(null);
  const [fetchedLanguages, setFetchedLanguages] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [languageError, setLanguageError] = useState("");
  const [avatarError, setAvatarError] = useState("");
  const [logoError, setLogoError] = useState("");
  const [organizationTypeError, setOrganizationTypeError] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoLink, setLogoLink] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [lodgingBusinessType, setLodgingBusinessType] = useState("");
  const [roomOrUnitLabel, setRoomOrUnitLabel] = useState("");
  const [lodgingBusinessTypeError, setLodgingBusinessTypeError] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const avatarPreviewUrlRef = useRef("");
  const logoPreviewUrlRef = useRef("");
  // True once the welcome message holds a user-typed or saved-bot value, so the
  // org-name default never overwrites a real customization ("defaults only").
  const welcomeDirtyRef = useRef(false);
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const { id: userId } = useParams();
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  // Org name from GET /user_organization (data.name) is the source of truth for
  // the property's identity; profileData is the /users/profile fallback.
  const resolvedOrgName =
    orgName ||
    profileData?.organization?.name ||
    profileData?.organization_name ||
    profileData?.first_name ||
    "";
  const defaultBotName = getDefaultBotName(resolvedOrgName);

  const replaceAvatarPreviewUrl = (nextUrl = "") => {
    if (avatarPreviewUrlRef.current) URL.revokeObjectURL(avatarPreviewUrlRef.current);
    avatarPreviewUrlRef.current = nextUrl;
  };

  const replaceLogoPreviewUrl = (nextUrl = "") => {
    if (logoPreviewUrlRef.current) URL.revokeObjectURL(logoPreviewUrlRef.current);
    logoPreviewUrlRef.current = nextUrl;
    setLogoPreviewUrl(nextUrl);
  };

  useEffect(
    () => () => {
      if (avatarPreviewUrlRef.current) URL.revokeObjectURL(avatarPreviewUrlRef.current);
      if (logoPreviewUrlRef.current) URL.revokeObjectURL(logoPreviewUrlRef.current);
    },
    []
  );

  // Pull the organization name (GET /user_organization → data.name) once on
  // mount. This is what seeds the bot name, welcome message, and QR subtitle.
  useEffect(() => {
    let active = true;
    const fetchOrganization = async () => {
      try {
        const token = sessionStorage.getItem("VendorToken");
        if (!token) return;
        const response = await _organizationDetails(token);
        const name = String(response?.data?.name || "").trim();
        if (active && name) setOrgName(name);
      } catch (_error) {
        // Non-fatal: fall back to profileData / generic defaults.
      }
    };
    fetchOrganization();
    return () => {
      active = false;
    };
  }, []);

  // Seed the welcome message from the org name — but only while it is still a
  // default (never after the user types or a saved bot loads a custom value).
  useEffect(() => {
    if (welcomeDirtyRef.current) return;
    setWelcomeMessage(buildWelcomeMessage(resolvedOrgName));
  }, [resolvedOrgName]);

  /* 1 ️⃣  — categorize once */
  // Owner-linking is a Plist Partner–only capability. Exact match against the
  // seeded vendor_categories.category_name ("Plist Partner Organization").
  const isPartnerCategoryId = (categoryId) =>
    categoryList?.some(
      (category) =>
        String(category.category_id) === String(categoryId) &&
        category.category_name === PARTNER_CATEGORY_NAME
    );
  const isPlistPartner = isPartnerCategoryId(organizationType);

  useEffect(() => {
    const timer = setTimeout(() => {
      setChatHeader(defaultBotName);
      setIsHeaderLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [defaultBotName]);

  useEffect(() => {
    if (!isHeaderLoading) {
      setChatHeader(selectedBot?.botname || defaultBotName);
    }
  }, [defaultBotName, isHeaderLoading, selectedBot?.botname]);

  useEffect(() => {
    if (isBotCreated) {
      seturlTag(username ? `${BASE_URL}/?bot=${username}` : `${BASE_URL}/?bot=${botId}`);
    }
  }, [isBotCreated, botId, username]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileError = getImageFileError(file);
    if (fileError) {
      setLogoError(fileError);
      e.target.value = "";
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    replaceLogoPreviewUrl(previewUrl);
    setLogo(file);
    setLogoLink("");
    setLogoError("");
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileError = getImageFileError(file);
    if (fileError) {
      setAvatarError(fileError);
      e.target.value = "";
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    replaceAvatarPreviewUrl(previewUrl);
    setAvatarFile(file);
    setSelectedAvatar(previewUrl);
    setAvatarError("");
  };

  const handleLogoLinkChange = (e) => {
    const nextLogoLink = e.target.value;
    replaceLogoPreviewUrl();
    setLogoLink(nextLogoLink);
    setLogo(null);
    setLogoError(
      isBrowserBlobUrl(nextLogoLink)
        ? "Browser preview URLs cannot be saved. Upload the original image file instead."
        : ""
    );
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleAudioToggle = (e) => {
    setAudioEnabled(e.target.checked);
  };

  const handleLodgingBusinessTypeChange = (e) => {
    const nextType = e.target.value;
    const selectedType = LODGING_BUSINESS_TYPES.find((type) => type.value === nextType);
    setLodgingBusinessType(nextType);
    setRoomOrUnitLabel(selectedType?.inventoryLabel || "");
    setLodgingBusinessTypeError("");
  };

  useEffect(() => {
    if (isBotCreated) {
      const token = sessionStorage.getItem("VendorToken");
      const scriptString = `<script src="${BASE_URL}/script/script.js" ${
        username ? `bot="${username}"` : `bot="${botId}"`
      }></script>`;
      setScriptTag(scriptString);
    }
  }, [isBotCreated, botId, username]);

  const handleUpdateBot = async () => {
    if (!logo && isBrowserBlobUrl(logoLink)) {
      setLogoError("Browser preview URLs cannot be saved. Upload the original image file instead.");
      return;
    }
    setLoading(true);
    try {
      const vendorToken = sessionStorage.getItem("VendorToken");
      const persistedData = JSON.parse(sessionStorage.getItem("botData"));
      const updatedData = {};
      if (chatHeader !== persistedData.botname) {
        updatedData.botname = chatHeader;
      }
      if (bgColor !== persistedData.bot_color_code) {
        updatedData.bot_color_code = bgColor;
      }
      if (welcomeMessage !== persistedData.welcome_message) {
        updatedData.welcome_message = welcomeMessage;
      }

      // Check and update the Bot Organization Type (ensure correct comparison)
      if (String(organizationType) !== String(persistedData.bot_category_id)) {
        updatedData.bot_category_id = String(organizationType);
      }
      const nextLodgingBusinessType = isPlistPartner ? lodgingBusinessType : "";
      const nextRoomOrUnitLabel = isPlistPartner ? roomOrUnitLabel : "";
      if ((persistedData.lodging_business_type || "") !== nextLodgingBusinessType) {
        updatedData.lodging_business_type = nextLodgingBusinessType || null;
      }
      if ((persistedData.room_or_unit_label || "") !== nextRoomOrUnitLabel) {
        updatedData.room_or_unit_label = nextRoomOrUnitLabel || null;
      }

      if (avatarFile) {
        updatedData.avatar_file = avatarFile;
      } else if (selectedAvatar !== persistedData.avatar && !selectedAvatar?.startsWith("blob:")) {
        updatedData.avatar = selectedAvatar;
      }
      const currentSupportedLangs = activeLanguages.map((language) => language.code).join(",");
      if (currentSupportedLangs !== persistedData.supported_langs) {
        updatedData.supported_langs = currentSupportedLangs;
      }
      if (defaultLanguage?.toLowerCase() !== persistedData.default_lang) {
        updatedData.default_lang = defaultLanguage?.toLowerCase();
      }
      if ((scriptTag || "") !== persistedData.script_tag) {
        updatedData.script_tag = scriptTag || "";
      }
      if (logo) {
        updatedData.logo_file = logo;
      } else if ((logoLink || "") !== (persistedData.logo || "")) {
        updatedData.logo = logoLink || null;
      }
      const response = await _update_bot(botId, updatedData, vendorToken);

      if (response.status) {
        const persistedUpdates = withoutImageFiles(updatedData);
        const updatedBotData = {
          ...persistedData,
          ...persistedUpdates,
          logo: response?.data?.logo || (!logo ? persistedUpdates.logo : persistedData.logo) || "",
          avatar:
            response?.data?.avatar ||
            (!avatarFile ? persistedUpdates.avatar : persistedData.avatar) ||
            "",
        };
        sessionStorage.setItem("botData", JSON.stringify(updatedBotData));
        if (response?.data?.logo) {
          setLogoLink(response.data.logo);
          setLogo(null);
          if (logoInputRef.current) {
            logoInputRef.current.value = "";
          }
        }
        if (response?.data?.avatar) {
          replaceAvatarPreviewUrl();
          setSelectedAvatar(response.data.avatar);
          setAvatarFile(null);
          if (avatarInputRef.current) {
            avatarInputRef.current.value = "";
          }
        }
        toast.success("Bot updated successfully!");
      } else {
        toast.error(response?.data?.message || "Failed to update bot");
      }
    } catch (error) {
      console.error("Error updating bot:", error);
      toast.error(error?.response?.data?.message || "An error occurred while updating the bot");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryList = async () => {
    try {
      const categories_response = await _vendorCategories();
      if (categories_response.status) {
        setCategoryList(categories_response?.data?.categories);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    handleCategoryList();
  }, []);

  const fetchSupportedLanguages = async () => {
    try {
      const token = sessionStorage.getItem("VendorToken");
      const response = await _getActiveLanguage({ accessToken: token });
      if (response.status) {
        setFetchedLanguages(response?.data);
      }
    } catch (error) {
      console.log("Error fetching languages:", error);
    }
  };

  const handleColorChange = (color) => {
    setBgColor(color);
    setSelectedColor(color);
  };

  const handleWelcomeMessageChange = (e) => {
    welcomeDirtyRef.current = true;
    setWelcomeMessage(e.target.value);
  };

  const handleChatHeaderChange = (e) => {
    setIsHeaderLoading(false);
    setChatHeader(e.target.value);
  };

  const handleAvatarSelection = (avatar) => {
    replaceAvatarPreviewUrl();
    setAvatarFile(null);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
    setSelectedAvatar(avatar);
    setAvatarError("");
  };

  const handleOrganizationTypeChange = (e) => {
    const isAdmin = JSON.parse(sessionStorage.getItem("isAdmin")); // Check admin status

    if (isBotCreated && !isAdmin) {
      toast.error("Only admins can change the organization type after the bot is created.");
      return; // Ignore changes
    }

    const nextOrganizationType = e.target.value;
    setOrganizationType(nextOrganizationType); // Update state if valid
    if (!isPartnerCategoryId(nextOrganizationType)) {
      setLodgingBusinessType("");
      setRoomOrUnitLabel("");
      setLodgingBusinessTypeError("");
    } else if (!lodgingBusinessType) {
      const defaultType = LODGING_BUSINESS_TYPES[0];
      setLodgingBusinessType(defaultType.value);
      setRoomOrUnitLabel(defaultType.inventoryLabel);
    }
  };

  const handleBotCreation = async () => {
    let hasError = false;
    setErrorMessage("");
    setAvatarError("");
    setLanguageError("");
    setLogoError("");
    setOrganizationTypeError("");
    setLodgingBusinessTypeError("");

    // Check if an avatar is selected
    if (!selectedAvatar) {
      setAvatarError("Please select an avatar.");
      hasError = true;
    }
    if (!organizationType) {
      setOrganizationTypeError("Please select a bot organization type.");
      hasError = true;
    }
    if (isPlistPartner && !lodgingBusinessType) {
      setLodgingBusinessTypeError("Please select a lodging business type.");
      hasError = true;
    }
    if (activeLanguages?.length === 0) {
      setLanguageError("Please choose at least one language.");
      hasError = true;
    }
    if (!logo && isBrowserBlobUrl(logoLink)) {
      setLogoError("Browser preview URLs cannot be saved. Upload the original image file instead.");
      hasError = true;
    }
    if (hasError) return;
    setLoading(true);
    try {
      const vendorToken = sessionStorage.getItem("VendorToken");

      const body = {
        botname: chatHeader,
        ...(logo ? { logo_file: logo } : logoLink ? { logo: logoLink } : {}),
        enable_audio_response: audioEnabled,
        tone: "formal",
        bot_behavior_prompt: "hello",
        bot_color_code: bgColor,
        currency: "ngn",
        lang: "english",
        welcome_message: welcomeMessage,
        bot_category_id: String(organizationType),
        ...(isPlistPartner
          ? {
              lodging_business_type: lodgingBusinessType,
              room_or_unit_label: roomOrUnitLabel,
            }
          : {}),
        ...(avatarFile ? { avatar_file: avatarFile } : { avatar: selectedAvatar }),
        supported_langs: activeLanguages.map((lang) => lang.code).join(","),
        default_lang: defaultLanguage.toLowerCase(),
        script_tag: scriptTag || "",
      };
      const response = await _botCreation({
        body,
        accessToken: vendorToken,
        userId,
      });

      if (response.status) {
        setIsBotCreated(true);
        const newBotId = response?.data?.bot_id;
        setBotId(newBotId);

        // Now, set the script tag immediately after the bot is created
        const scriptString = `<script src="${BASE_URL}/script/script.js" ${
          username ? `bot="${username}"` : `bot="${newBotId}"`
        }></script>`;

        setScriptTag(scriptString);
        _update_bot(newBotId, { script_tag: scriptString }, vendorToken);

        // Store the bot data in localStorage
        const persistedBody = withoutImageFiles(body);
        const persistedBotData = {
          ...persistedBody,
          avatar: response?.data?.avatar || (!avatarFile ? selectedAvatar : ""),
          logo: response?.data?.logo || logoLink || "",
          script_tag: scriptString,
        };
        sessionStorage.setItem("botData", JSON.stringify(persistedBotData));
        if (response?.data?.logo) {
          setLogoLink(response.data.logo);
          setLogo(null);
          if (logoInputRef.current) {
            logoInputRef.current.value = "";
          }
        }
        if (response?.data?.avatar) {
          replaceAvatarPreviewUrl();
          setSelectedAvatar(response.data.avatar);
          setAvatarFile(null);
          if (avatarInputRef.current) avatarInputRef.current.value = "";
        }
      } else {
        setIsBotCreated(false);
        toast.error(response?.data?.message);
      }
    } catch (error) {
      setIsBotCreated(false);
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(scriptTag);
    toast.success("Script copied to clipboard!");
  };

  const handleCopyURL = () => {
    if (!urlTag) {
      toast.error("URL is not available to copy.");
      return;
    }
    navigator.clipboard
      .writeText(urlTag)
      .then(() => {
        toast.success("URL Copied");
      })
      .catch((err) => {
        console.error("Failed to copy URL:", err);
        toast.error("Failed to copy the URL.");
      });
  };

  const openLanguageModal = () => {
    setIsModalOpen(true);
    if (fetchedLanguages.length === 0) {
      fetchSupportedLanguages();
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleLanguageClick = (selectedLanguage) => {
    const currentActiveLanguages = activeLanguages || [];

    const isLanguageActive = currentActiveLanguages.some(
      (language) => language.code === selectedLanguage.code
    );

    if (isLanguageActive) {
      setActiveLanguages(
        currentActiveLanguages.filter((language) => language.code !== selectedLanguage.code)
      );
    } else {
      setActiveLanguages([...currentActiveLanguages, selectedLanguage]);
    }
    setLanguageError("");
  };

  async function getBotDetails() {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("VendorToken");
      const botDetails = await _getBotDetails({ accessToken: token, userId });
      if (botDetails?.status) {
        sessionStorage.setItem("botData", JSON.stringify(botDetails.data));
        setIsBotCreated(true);
        setSelectedBot(botDetails.data);
        const userData = JSON.parse(sessionStorage.getItem("userData"));
        if (botDetails.data?.botname) {
          setChatHeader(botDetails.data.botname);
        } else {
          setChatHeader(
            getDefaultBotName(
              botDetails.data?.organization?.name ||
                resolvedOrgName ||
                profileData?.organization?.name ||
                profileData?.organization_name ||
                profileData?.first_name
            )
          );
        }
        setSelectedAvatar(botDetails.data.avatar);
        setOrganizationType(String(botDetails.data.bot_category));
        setBgColor(botDetails.data.bot_color_code);
        // A saved bot's welcome message is a real customization — apply it and
        // lock it so the org-name default can't overwrite it. If the bot has no
        // saved message, leave the org-seeded default in place.
        if (String(botDetails.data.welcome_message || "").trim()) {
          welcomeDirtyRef.current = true;
          setWelcomeMessage(botDetails.data.welcome_message);
        }
        setLogoLink(botDetails.data.logo);
        setLodgingBusinessType(botDetails.data.lodging_business_type || "");
        setRoomOrUnitLabel(botDetails.data.room_or_unit_label || "");
        setAudioEnabled(botDetails.data.enable_audio_response);
        if (Array.isArray(botDetails.data.supported_langs)) {
          setActiveLanguages(botDetails.data.supported_langs);
        }

        // Set default language, ensuring it matches the format from the API
        if (botDetails.data.default_lang) {
          setDefaultLanguage(botDetails.data.default_lang); // This is already in the correct format ('en')
        }
        setScriptTag(botDetails.data.script_tag);
        setErrorMessage("");
        setBotId(botDetails.data.bot_id);
        setUsername(botDetails.data.username);
      }
    } catch (error) {
      if (error?.response?.status === 404 || error?.status === 404) {
        sessionStorage.removeItem("botData");
        setIsBotCreated(false);
        setSelectedBot(null);
        setBotId(undefined);
        toast.info("No chatbot has been created yet. Set up your bot to continue.");
      } else if (error?.response?.data?.message == "User not found with id: " + userId) {
        window.location.href = "/";
      } else {
        toast.error(error?.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (categoryList?.length > 0) {
      getBotDetails();
    }
  }, [categoryList]);

  // Update selectedCategoryName when organizationType or categoryList changes
  useEffect(() => {
    if (organizationType && categoryList.length > 0) {
      const selectedCategory = categoryList.find(
        (cat) => String(cat.category_id) === String(organizationType)
      );
      if (selectedCategory) {
        setSelectedCategoryName(selectedCategory.category_name);
      } else {
        setSelectedCategoryName("");
      }
    }
  }, [organizationType, categoryList]);

  const handleOwnersChange = ({ owner_links }) => {
    setSelectedBot((prev) => (prev ? { ...prev, owner_links } : prev));
    updateStoredBot({ owner_links });
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setUsername(newUsername);
    setUsernameError(""); // Clear any previous errors
  };

  // Function to validate username
  const validateUsername = () => {
    if (!username) {
      setUsernameError("Username is required");
      return false;
    }

    // Username validation rules
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(username)) {
      setUsernameError(
        "Username must be 3-20 characters long and can only contain letters, numbers, and underscores"
      );
      return false;
    }

    return true;
  };

  // Function to update username

  // Function to update username
  const handleUpdateUsername = async () => {
    // Validate username first
    if (!validateUsername()) {
      return;
    }
    setLoading(true);
    try {
      const vendorToken = sessionStorage.getItem("VendorToken");

      const response = await _vendorUserName({
        body: { username },
        accessToken: vendorToken,
        user_id: userId,
      });

      if (response.status) {
        toast.success("Username updated successfully!");

        // Store username in localStorage
        const userData = JSON.parse(sessionStorage.getItem("userData")) || {};
        userData.username = username;
        sessionStorage.setItem("userData", JSON.stringify(userData));
      } else {
        toast.error(response?.data?.message || "Failed to update username");
        setUsernameError(response?.data?.message || "Username update failed");
      }
    } catch (error) {
      console.error("Error updating username:", error);
      toast.error(
        error?.response?.data?.message || "An error occurred while updating the username"
      );
      setUsernameError(error?.response?.data?.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const userData = JSON.parse(sessionStorage.getItem("userData"));
  useEffect(() => {
    // Check localStorage for username on component mount
    if (userData && userData.username) {
      setUsername(userData.username);

      // setIsUsernamePermanent(true); // Assume it's permanent if it exists in localStorage
    }
  }, []);

  useEffect(() => {
    if (activeLanguages?.length > 0 && (!defaultLanguage || defaultLanguage === "")) {
      setDefaultLanguage(activeLanguages[0]?.code);
    }
  }, [activeLanguages, defaultLanguage]);

  return (
    <>
      <div className="">
        {loading ? (
          <div className="flex h-screen items-center justify-center">
            <p>Loading...</p> {/* Replace with a spinner component if needed */}
          </div>
        ) : (
          <>
            <ToastContainer />
            {/* <div className="tabsView">
          <button
            className={activeTab === "dashboard" ? "active" : ""}
            onClick={() => handleTabChange("dashboard")}
          >
            Bot Appearance
          </button>
          <button
            className={activeTab === "knowledge" ? "active" : ""}
            onClick={() => handleTabChange("knowledge")}
          >
            Bot Knowledge
          </button>
        </div> */}

            <div>
              {activeTab === "dashboard" && (
                <div>
                  <div className="mx-auto flex justify-between">
                    <div className="w-full">
                      <div className="mt-4">
                        <div className="rounded-md bg-white p-4 shadow">
                          <h1 className="text-xl text-[#666]">
                            <b className="align-center flex gap-2">
                              <FaRegUserCircle /> Identity
                            </b>
                          </h1>
                          <hr className="my-3" />
                          <div className="mb-4">
                            <label className="text-black-800 block font-bold">Username</label>
                            <div className="flex items-center gap-2">
                              <input
                                required
                                type="text"
                                className={`mt-1 w-full rounded border p-2 ${
                                  usernameError ? "border-red-500" : ""
                                }`}
                                placeholder="Enter your username"
                                value={username}
                                onChange={handleUsernameChange}
                              />
                              <button
                                onClick={handleUpdateUsername}
                                className="mt-1 whitespace-nowrap rounded bg-blue-500 px-4 py-2 text-white"
                              >
                                Save
                              </button>
                            </div>
                            {usernameError && (
                              <div className="mt-2">
                                <p className="text-sm text-red-500">{usernameError}</p>
                              </div>
                            )}
                            {/* <div
                          style={{
                            fontSize: "12px",
                            letterSpacing: "1px",
                            color: "gray",
                          }}
                        >
                          {isUsernamePermanent
                            ? "Your username is set and cannot be changed"
                            : "This can be updated only once"}
                        </div> */}
                          </div>
                          <div className="mb-4">
                            <label className="text-black-800 block font-bold">BOT Name</label>
                            <input
                              required
                              type="text"
                              className="mt-1 w-full rounded border p-2"
                              placeholder={defaultBotName}
                              value={isHeaderLoading ? "Loading..." : chatHeader}
                              onChange={handleChatHeaderChange}
                              disabled={isHeaderLoading}
                            />
                          </div>
                          <div className="mb-4">
                            <label className="text-black-800 block font-bold">
                              Audio Responses
                            </label>
                            <div className="mt-1">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={audioEnabled}
                                  onChange={handleAudioToggle}
                                />
                                <span className="ml-1">Enable Audio Responses</span>
                              </label>
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="text-black-800 block font-bold">
                              Welcome Message
                            </label>
                            <textarea
                              className="mt-1 w-full rounded border p-2"
                              placeholder="Hi! How can we help?"
                              value={welcomeMessage}
                              onChange={handleWelcomeMessageChange}
                            />
                          </div>
                          <div className="mt-4">
                            <label className="text-black-800 block font-bold">
                              Bot Organization Type
                            </label>
                            <select
                              className="mt-1 w-full rounded border p-2"
                              value={organizationType}
                              onChange={handleOrganizationTypeChange}
                              disabled={
                                isBotCreated && !JSON.parse(sessionStorage.getItem("isAdmin"))
                              }
                            >
                              <option value="" disabled>
                                Select Organization Type
                              </option>
                              {categoryList?.map((type, index) => (
                                <option key={index} value={type?.category_id}>
                                  {type?.category_name}
                                </option>
                              ))}
                            </select>
                            {organizationTypeError && (
                              <div className="mt-2">
                                <p className="text-sm text-red-500">{organizationTypeError}</p>
                              </div>
                            )}
                          </div>
                          {isPlistPartner && (
                            <div className="border-emerald-100 bg-emerald-50/50 mt-4 rounded-md border p-3">
                              <label className="text-black-800 block font-bold">
                                Lodging Business Type
                              </label>
                              <select
                                className="mt-1 w-full rounded border p-2"
                                value={lodgingBusinessType}
                                onChange={handleLodgingBusinessTypeChange}
                              >
                                <option value="" disabled>
                                  Select lodging type
                                </option>
                                {LODGING_BUSINESS_TYPES.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                              {lodgingBusinessTypeError && (
                                <div className="mt-2">
                                  <p className="text-sm text-red-500">{lodgingBusinessTypeError}</p>
                                </div>
                              )}

                              <label className="text-black-800 mt-3 block font-bold">
                                Room / Unit Label
                              </label>
                              <input
                                type="text"
                                className="mt-1 w-full rounded border p-2"
                                placeholder="Room"
                                value={roomOrUnitLabel}
                                onChange={(event) => setRoomOrUnitLabel(event.target.value)}
                              />
                              <p className="mt-2 text-sm text-gray-500">
                                Used to personalize customer-facing labels, for example Room, Suite,
                                Apartment, Villa, Bed, or Unit.
                              </p>
                            </div>
                          )}
                          {isPlistPartner && (
                            <OwnerEmailLinkForm
                              botId={botId}
                              ownerLinks={selectedBot?.owner_links}
                              onOwnersChange={handleOwnersChange}
                            />
                          )}
                        </div>
                        <div className="mt-4 rounded-md bg-white p-4 shadow">
                          <h1 className="text-xl text-[#666]">
                            <b className="align-center flex gap-2">
                              <FaUserEdit /> Customisation
                            </b>
                          </h1>
                          <hr className="my-3" />

                          <div className="pb-4">
                            <div>
                              <b>Choose theme</b>
                            </div>
                            <div className="">
                              <div className="themeHover ml-auto flex space-x-2">
                                <div
                                  className={`h-8 w-8 cursor-pointer rounded-full bg-blue-500 ${
                                    selectedColor === "#3B82F6" ? "active" : ""
                                  }`}
                                  onClick={() => handleColorChange("#3B82F6")}
                                ></div>
                                <div
                                  className={`h-8 w-8 cursor-pointer rounded-full bg-red-500 ${
                                    selectedColor === "#EF4444" ? "active" : ""
                                  }`}
                                  onClick={() => handleColorChange("#EF4444")}
                                ></div>
                                <div
                                  className={`h-8 w-8 cursor-pointer rounded-full bg-green-500 ${
                                    selectedColor === "#10B981" ? "active" : ""
                                  }`}
                                  onClick={() => handleColorChange("#10B981")}
                                ></div>
                                <div
                                  className={`h-8 w-8 cursor-pointer rounded-full bg-purple-500 ${
                                    selectedColor === "#8B5CF6" ? "active" : ""
                                  }`}
                                  onClick={() => handleColorChange("#8B5CF6")}
                                ></div>
                                <input
                                  type="color"
                                  className={`h-8 w-8 cursor-pointer ${
                                    selectedColor === bgColor ? "active" : ""
                                  }`}
                                  value={bgColor}
                                  onChange={(e) => handleColorChange(e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="mb-4 rounded bg-[#E0E5F2] p-2.5">
                            <label className="text-black-800 block font-bold">Logo</label>
                            <div className="mt-1">
                              <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                onChange={handleLogoUpload}
                                className="w-full rounded border p-2"
                              />
                              {logoError ? (
                                <p className="mt-2 text-sm text-red-500">{logoError}</p>
                              ) : null}
                              <p className="my-3 text-center text-gray-900">OR</p>
                              <input
                                type="text"
                                placeholder="Enter logo URL"
                                value={logoLink}
                                onChange={handleLogoLinkChange}
                                className="mt-1 w-full rounded border p-2"
                              />
                            </div>
                          </div>
                          <label className="text-black-800 block font-bold">Select Avatar</label>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {avatarOptions.map((avatar, index) => (
                              <img
                                key={index}
                                src={avatar}
                                alt={`Avatar ${index + 1}`}
                                className={`h-12 w-12 cursor-pointer rounded-full border-2 ${
                                  selectedAvatar === avatar ? "border-blue-500" : "border-gray-300"
                                }`}
                                onClick={() => handleAvatarSelection(avatar)}
                              />
                            ))}
                            {selectedAvatar && !avatarOptions.includes(selectedAvatar) && (
                              <img
                                src={selectedAvatar}
                                alt="Custom Avatar"
                                className="h-12 w-12 rounded-full border-2 border-blue-500 object-cover"
                              />
                            )}
                          </div>
                          <div className="mt-3">
                            <label className="text-black-700 block text-sm font-semibold">
                              Or upload a custom avatar
                            </label>
                            <input
                              ref={avatarInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleAvatarUpload}
                              className="mt-1 w-full rounded border p-2"
                            />
                          </div>
                          <div>
                            {avatarError && (
                              <div className="mt-2">
                                <p className="text-sm text-red-500">{avatarError}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 rounded-md bg-white p-4 shadow">
                          <h1 className="text-xl text-[#666]">
                            <b className="align-center flex gap-2">
                              <IoIosGlobe /> Language
                            </b>
                          </h1>
                          <hr className="my-3" />
                          <div className="languageParent">
                            <div>
                              <label className="text-black-800 block cursor-pointer font-bold">
                                Choose Language
                              </label>
                              <div
                                className="langSelect align-center flex gap-2 align-middle"
                                onClick={openLanguageModal}
                              >
                                <label className="">Languages</label>
                                <FaGlobe />
                              </div>

                              {isModalOpen && (
                                <div className="modal-overlay">
                                  <div className="modal">
                                    <h1>
                                      <b>Select multiple languages</b>
                                    </h1>
                                    <br />
                                    <button className="close" onClick={closeModal}>
                                      <IoMdClose />
                                    </button>
                                    <div className="languageList">
                                      <ul>
                                        {fetchedLanguages.map((language) => (
                                          <li
                                            key={language.code}
                                            className={
                                              activeLanguages?.some(
                                                (activeLang) => activeLang.code === language.code
                                              )
                                                ? "active"
                                                : ""
                                            }
                                            onClick={() => handleLanguageClick(language)}
                                          >
                                            {language.name} ({language.code})
                                          </li>
                                        ))}
                                      </ul>
                                    </div>

                                    {loading && <div className="LangLoader">Saving...</div>}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div>
                              <div>
                                <b>Default language</b>
                              </div>
                              <select
                                className="Deflanguage"
                                value={defaultLanguage}
                                onChange={(e) => setDefaultLanguage(e.target.value)}
                              >
                                <option value="" disabled>
                                  Default Lang
                                </option>
                                {activeLanguages?.map((language) => (
                                  <option
                                    key={language.code}
                                    value={language.code} // This should match the default_lang format
                                  >
                                    {language.name} ({language.code})
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div style={{ marginTop: "", paddingLeft: "" }}>
                            {languageError && (
                              <div className="mt-2 ">
                                <p className="text-sm text-red-500">{languageError}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        {isBotCreated ? (
                          <div className="mt-4 rounded-md bg-white p-4 shadow">
                            <h1 className="text-xl text-[#666]">
                              <b className="align-center flex items-center gap-2">
                                <FaRobot /> Bot Knowledge
                              </b>
                            </h1>
                            <hr className="my-3" />
                            <KnowledgeSourcesPanel botId={botId} refreshKey={knowledgeRefreshKey} />
                          </div>
                        ) : (
                          <div className="mt-4 rounded-md bg-gray-100 p-4 shadow">
                            <h1 className="text-xl text-[#666]">
                              <b className="align-center flex gap-2">
                                <FaRobot /> Bot Knowledge
                              </b>
                            </h1>
                            <hr className="my-3" />
                            <p className="text-gray-500">
                              Bot Knowledge is disabled until the bot is created.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-full min-w-0">
                      <div
                        className="sticky flex max-h-[calc(100vh-81px)] flex-col items-center overflow-y-auto p-4"
                        style={{ top: "65px" }}
                      >
                        {/* Bot Agent Preview */}
                        <div
                          className="flex min-h-[20rem] w-80 shrink-0 flex-col rounded-lg bg-white shadow-md"
                          style={{ width: "400px" }}
                        >
                          <div
                            className="rounded-t-xl p-4 text-center text-white"
                            style={{ backgroundColor: bgColor }}
                          >
                            {chatHeader}
                          </div>
                          <div className="flex-1 p-2.5" style={{ minHeight: "200px" }}>
                            <div className="max-w-60 flex w-4/5">
                              {selectedAvatar && (
                                <img
                                  src={selectedAvatar}
                                  alt="Selected Avatar"
                                  className="inline-block h-8 w-8 rounded-full"
                                />
                              )}
                              <div className="ml-1 mr-1 rounded-3xl rounded-tl-none bg-[#f1f1f1] p-3 text-xs">
                                {tokenizeMessage(welcomeMessage).map((token, index) => {
                                  const key = `${token.type}-${index}`;
                                  if (token.type === "lineBreak") return <br key={key} />;
                                  if (token.type === "heading")
                                    return <h3 key={key}>{token.value}</h3>;
                                  if (token.type === "bold")
                                    return <strong key={key}>{token.value}</strong>;
                                  if (token.type === "italic")
                                    return <em key={key}>{token.value}</em>;
                                  if (token.type === "underline")
                                    return <u key={key}>{token.value}</u>;
                                  return <Fragment key={key}>{token.value}</Fragment>;
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="mt-4">
                            <input
                              type="text"
                              disabled
                              className="w-full rounded p-2 text-sm"
                              placeholder="Type here and press enter..."
                            />
                          </div>
                        </div>

                        <div className="mt-4 flex w-full	flex-col items-center justify-center p-2">
                          {isBotCreated ? (
                            <button
                              onClick={handleUpdateBot}
                              className="rounded-md bg-blue-500 py-2 px-4 text-white"
                              style={{ width: "400px" }}
                            >
                              Update Bot
                            </button>
                          ) : (
                            <button
                              onClick={handleBotCreation}
                              className="rounded-md bg-blue-500 py-2 px-4 text-white"
                              style={{ width: "400px" }}
                            >
                              Create Bot
                            </button>
                          )}
                        </div>

                        {isBotCreated && (
                          <div
                            className="mt-4 shrink-0 rounded-md bg-white p-4 shadow"
                            style={{ width: "400px" }}
                          >
                            {/* <p className="text-sm">
                          Your bot has been created with ID: <b> {botId}</b>
                        </p> */}
                            <p className="text-sm font-bold">Embed this script on your site:</p>
                            <div style={{ position: "relative" }}>
                              <div
                                className="rounded bg-gray-100 p-2"
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "auto",
                                  paddingRight: "55px",
                                }}
                              >
                                <code className="whitespace-break-spaces text-xs">{scriptTag}</code>
                              </div>
                              <button
                                style={{
                                  position: "absolute",
                                  right: "0",
                                  top: "0",
                                  height: "100%",
                                }}
                                onClick={handleCopyScript}
                                className="rounded bg-green-500 py-2 px-4 text-sm text-white"
                              >
                                <FaRegCopy />
                              </button>
                            </div>

                            <p className="mt-2 text-sm font-bold">Chat widget URL</p>
                            <div style={{ position: "relative" }}>
                              <div
                                className="rounded bg-gray-100 p-2"
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "auto",
                                  paddingRight: "55px",
                                }}
                              >
                                <code className="whitespace-break-spaces text-xs">{urlTag}</code>
                              </div>
                              <button
                                style={{
                                  position: "absolute",
                                  right: "0",
                                  top: "0",
                                  height: "100%",
                                }}
                                onClick={handleCopyURL}
                                className="rounded bg-green-500 py-2 px-4 text-sm text-white"
                              >
                                <FaRegCopy />
                              </button>
                            </div>

                            {/* <a 
                          className="mt-3 bg-green-500"
                          style={{padding:'8px 10px',color:'white', display:'inline-block', width:'100%', textAlign:'center', borderRadius:'0.375rem'}}
                        href={`https://static-plist-staging.edully.com/?bot-id=${botId}`} target="_blank"></a> */}
                            <p
                              style={{
                                color: "#666",
                                fontSize: "12px",
                                letterSpacing: "0.2px",
                              }}
                            >
                              This link will generate the bot{" "}
                            </p>

                            <BotQrCard
                              url={urlTag}
                              color={bgColor}
                              image={logoPreviewUrl || logoLink}
                              botName={chatHeader}
                              organizationName={
                                selectedBot?.organization?.name ||
                                resolvedOrgName ||
                                (profileData?.first_name
                                  ? `${profileData.first_name}'s hotel`
                                  : "Hotel guest assistant")
                              }
                              fileName={`plist-${username || botId || "hotel"}-qr`}
                            />
                          </div>
                        )}
                        <br />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Marketplace;
